import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { JOURNEE_PAR_DEFAUT } from '@nextilt/commun';
import { EchecApi, api } from '../../partage/api/client';
import type { CourageDeMaintenant, Creneau, Ou, PossibleDuJour, TacheEnvoyee } from '../../partage/api/types';
import { JOURNEE_PRETE, journeePreteDu } from '../../partage/vocabulaire';
import { enMots, messageDuJour } from './refusDuJour';
import { cles, relireApres } from '../../partage/relectures';
import { jourEnToutesLettres } from './datesDeLAgenda';
import { fixer, retirer, versLaRoute, type Choix } from './journeeEnCours';
import { avecLesFaites } from './journeeDuMoment';
import { tacheAAjouter } from './propositionDuJour';

/**
 * La journée qu'on compose, écrite une seule fois pour l'ordinateur (« Préparer ma journée ») et
 * pour les trois écrans du téléphone (« Propose-moi une journée », « Je choisis moi-même »,
 * « Changer ma journée ») : chantier `nettoyage-journee`, ticket 02. Chacun n'a plus que son rendu
 * et ce qui lui est propre.
 *
 * Les choix restent à l'écran ; le serveur range (les heures, les rendez-vous, le temps libre) à
 * chaque changement ; une heure fixée part d'abord au serveur, refusée rien ne change ; rien ne
 * s'écrit avant « C'est ma journée ».
 *
 * `choix` est nul tant que la journée de départ n'est pas posée (`demarrer`) : la proposition
 * arrive du serveur ; « Je choisis » et l'ordinateur partent d'une journée vide.
 */

/** Les réglages d'une journée qu'on compose : `ou` nul, le serveur choisit (l'ordinateur). */
export interface ReglagesAComposer {
  debut: string;
  tempsMin: number;
  ou: Ou | null;
  /** Le courage, au téléphone seulement : il entre dans la clé de la préparation et s'enregistre. */
  courage?: CourageDeMaintenant;
}

export function useJourneeAComposer({
  jour,
  reglages,
  onFermer,
  depart = null,
  gardees = [],
  actif = true,
  rangementFige = false,
}: {
  jour: string;
  reglages: ReglagesAComposer;
  onFermer: (message: string) => void;
  /** La journée de départ quand elle est connue d'avance (vide pour « Je choisis » et l'ordinateur). */
  depart?: Choix[] | null;
  /**
   * Les tâches déjà faites aujourd'hui, gardées à leur heure quand on recommence sa journée au
   * téléphone : rangées et enregistrées avec elle, sans geste, hors du temps compté.
   */
  gardees?: readonly Choix[];
  /** Le serveur range seulement quand c'est vrai (l'ordinateur : une fois la journée lue). */
  actif?: boolean;
  /**
   * Un rangement reçu ne se relit pas tout seul (au téléphone, où la proposition donne déjà ses
   * heures) ; à l'ordinateur, il se relit comme toute lecture, au retour sur la fenêtre.
   */
  rangementFige?: boolean;
}) {
  const client = useQueryClient();
  const { debut, tempsMin, ou, courage } = reglages;
  const [choix, setChoix] = useState<Choix[] | null>(depart);
  const [enHeure, setEnHeure] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const liste = choix ?? [];
  const envoyer = (suite: readonly Choix[]) => versLaRoute(avecLesFaites(gardees, suite));
  const envoyees = envoyer(liste);
  const cleDuRangement = (taches: TacheEnvoyee[]) => cles.rangement(jour, debut, taches);

  // Ce que le monde impose ce jour-là, la journée déjà là, ce qui sera possible ; au téléphone, au
  // courage répondu.
  const preparation = useQuery({
    queryKey: courage === undefined ? cles.preparation(jour, ou) : cles.preparation(jour, ou, courage),
    queryFn: () => api.preparation(jour, ou ?? undefined, courage),
    placeholderData: (avant) => avant,
  });
  const donnees = preparation.data;
  const jourPasse = donnees ? donnees.jour < donnees.aujourdhui : false;

  /*
   * La version de la journée d'où part ce qu'on compose (chantier nettoyage-regles, ticket 04) :
   * prise à la première lecture fraîche de ce jour, `null` s'il était vide, et envoyée avec
   * « C'est ma journée ». Si la journée a changé depuis sur un autre appareil, le serveur refuse
   * au lieu de l'effacer. `undefined` tant qu'elle n'est pas lue : la journée part sans version,
   * comme avant. Gardée avec son jour : un autre jour reprend la sienne.
   */
  const [prise, setPrise] = useState<{ jour: string; version: string | null } | null>(null);
  const versionLue = prise?.jour === jour ? prise.version : undefined;
  const lectureFraiche = !!donnees && donnees.jour === jour && !preparation.isFetching && !preparation.isPlaceholderData;
  useEffect(() => {
    if (versionLue !== undefined || !lectureFraiche) return;
    setPrise({ jour, version: donnees?.journee?.version ?? null });
  }, [versionLue, lectureFraiche, donnees, jour]);

  const rangement = useQuery({
    queryKey: cleDuRangement(envoyees),
    queryFn: () => api.rangerLeJour(jour, { debut, taches: envoyees }),
    enabled: actif && !jourPasse && envoyees.length > 0,
    staleTime: rangementFige ? Infinity : 0,
    placeholderData: (avant) => avant,
    retry: false,
  });

  /*
   * Une heure fixée qui tombe maintenant sur autre chose (un rendez-vous posé depuis, ou après un
   * ↻) : le serveur refuse de ranger. Elle repart à la suite, et la phrase dit pourquoi. Tout
   * autre refus du rangement (une tâche bloquée entre-temps, minuit passé) ne change rien aux
   * choix : il se dit par `message`, des deux côtés (ticket 03).
   */
  const refusDuRangement = rangement.error;
  useEffect(() => {
    if (!(refusDuRangement instanceof EchecApi) || refusDuRangement.code !== 'heure_fixee_chevauche') return;
    const id = (refusDuRangement.details as { tache?: { id?: string } } | undefined)?.tache?.id;
    if (!id) return;
    setMessage(enMots(refusDuRangement));
    setChoix((avant) => (avant ? fixer(avant, id, null) : avant));
  }, [refusDuRangement]);

  /** Relire ce que la journée écrite change, puis fermer avec la phrase. */
  async function relireEtFermer(texte: string) {
    await relireApres(client, 'composerUneJournee');
    onFermer(texte);
  }

  const enregistrer = useMutation({
    mutationFn: () =>
      api.enregistrerLeJour(jour, { debut, tempsMin, ou: ou ?? JOURNEE_PAR_DEFAUT.ou, courage, taches: envoyees, version: versionLue }),
    onSuccess: () =>
      relireEtFermer(donnees && donnees.jour === donnees.aujourdhui ? JOURNEE_PRETE : journeePreteDu(jourEnToutesLettres(jour))),
    onError: async (e) => {
      setMessage(enMots(e));
      if (!(e instanceof EchecApi) || e.code !== 'journee_changee') return;
      // La journée a changé ailleurs : elle se relit, et sa nouvelle version se reprend. Les choix
      // à l'écran restent ; « C'est ma journée » les enregistre alors sur la journée relue.
      try {
        await relireApres(client, 'composerUneJournee');
      } finally {
        setPrise(null);
      }
    },
  });

  /** La journée de départ, et ses heures si le serveur les a déjà dites. */
  function demarrer(suite: Choix[], range?: { creneaux: Creneau[]; tientDansLeJour: boolean }) {
    setChoix(suite);
    // Les heures de la proposition ne savent rien des faites gardées : le serveur range alors tout.
    if (range && gardees.length === 0) client.setQueryData(cleDuRangement(versLaRoute(suite)), range);
  }

  function toucher(suite: Choix[]) {
    setChoix(suite);
    setMessage(null);
  }

  /**
   * Une heure fixée part d'abord au serveur, sans rien écrire : s'il la refuse (elle tomberait sur
   * un rendez-vous ou une autre heure fixée), rien ne change et la phrase le dit.
   */
  async function fixerLHeure(id: string, heure: string | null) {
    setEnHeure(null);
    const suite = fixer(liste, id, heure);
    if (heure === null) {
      toucher(suite);
      return;
    }
    const taches = envoyer(suite);
    try {
      const range = await api.rangerLeJour(jour, { debut, taches });
      client.setQueryData(cleDuRangement(taches), range);
      toucher(suite);
    } catch (e) {
      setMessage(enMots(e));
    }
  }

  /** + : la tâche s'ajoute à la suite si elle tient dans ce qui reste ; sinon la phrase le dit. Vrai si ajoutée. */
  function ajouterLaTache(p: Pick<PossibleDuJour, 'id' | 'minutes'>, reste: number): boolean {
    const { choix: suite, message: pourquoi } = tacheAAjouter(liste, p, reste);
    if (pourquoi) {
      setMessage(pourquoi);
      return false;
    }
    toucher(suite);
    return true;
  }

  /** × : la tâche quitte la journée, les heures suivent. */
  function retirerLaTache(id: string) {
    setEnHeure(null);
    toucher(retirer(liste, id));
  }

  // La journée rangée ; sans tâche, les rendez-vous seuls, à leur heure.
  const creneaux: Creneau[] =
    envoyees.length > 0
      ? (rangement.data?.creneaux ?? [])
      : [...(donnees?.impose.rendezVous ?? [])]
          .sort((a, b) => a.heure.localeCompare(b.heure))
          .map((r) => ({
            sorte: 'rendez_vous' as const,
            id: r.id,
            titre: r.titre,
            heure: r.heure,
            fin: r.heure,
            minutes: r.dureeMin ?? 0,
            horsDuJour: false,
            fixee: false,
            libreApres: null,
          }));

  /** « C'est ma journée » se touche : une tâche choisie au moins, rangée, qui tient avant minuit. */
  const pret = liste.length > 0 && !!rangement.data?.tientDansLeJour && !rangement.isPlaceholderData && !enregistrer.isPending;
  /** La journée rangée finirait après minuit : la phrase le dit. */
  const apresMinuit = !!rangement.data && liste.length > 0 && !rangement.data.tientDansLeJour;

  return {
    choix,
    liste,
    demarrer,
    toucher,
    /** La phrase sous la journée : celle du dernier geste, sinon le refus du rangement (ticket 03). */
    message: messageDuJour(message, refusDuRangement),
    setMessage,
    enHeure,
    setEnHeure,
    fixerLHeure,
    ajouterLaTache,
    retirerLaTache,
    preparation,
    donnees,
    rangement,
    creneaux,
    enregistrer,
    relireEtFermer,
    pret,
    apresMinuit,
  };
}
